// src/services/auctionService.ts

import { Cookies } from "react-cookie";

/* ----------------------------- TYPES ----------------------------- */

export interface Auction {
  id: string;
  shirt_id: string;
  seller_id: string;
  starting_price: number;
  current_price: number;
  reserve_price?: number | null;
  end_time: string;
  status: "active" | "ended" | "sold" | string;
  winner_id?: string | null;
  bids_count?: number;
  shirt?: import("../types/Shirt").ApiShirt;
}

export interface Bid {
  id: string;
  auction_id?: string;
  bidder_id?: string;
  initials?: string;
  obfuscated_name?: string;
  country?: string | null;
  amount: number;
  created_at: string;
}

export interface LiveBid {
  amount: number;
  bidder_id: string;
  initials?: string;
  obfuscated_name: string;
  timestamp: string | null;
  country?: string | null;
}

export interface AuctionStateMessage {
  type: "auction_state" | "new_bid";
  current_price: number;
  end_time: string | null;
  status: string;
  bids: LiveBid[];
}

export interface NewBidMessage {
  type: "new_bid";
  amount: number;
  bidder_id?: string;
  timestamp?: string | null;
  initials?: string;
  obfuscated_name?: string;
  country?: string | null;
  end_time?: string | null;
}

export interface AuctionExtendedMessage {
  type: "auction_extended";
  new_end_time: string;
  message?: string;
}

export type AuctionLiveMessage =
  | AuctionStateMessage
  | NewBidMessage
  | AuctionExtendedMessage
  | { type?: string; [key: string]: unknown };

export interface AuctionLiveState {
  currentPrice: number;
  endTime: string | null;
  status: string;
  bids: LiveBid[];
  highestBid: LiveBid | null;
}

export interface LiveBidsHandlers {
  onState?: (state: AuctionLiveState) => void;
  onMessage?: (message: AuctionLiveMessage) => void;
  onError?: (event: Event) => void;
  onClose?: (event: CloseEvent) => void;
}

export interface MyAuctionHistory {
  created: Auction[];
  active_bids: Auction[];
  won: Auction[];
}

export interface CreateAuctionPayload {
  shirt_id: string;
  starting_price: number;
  end_time: string;
  reserve_price?: number;
}

export interface CheckoutAuctionEscrowPayload {
  auction_id: string;
  shipping_name: string;
  shipping_street: string;
  shipping_number: string;
  shipping_box?: string;
  shipping_city: string;
  shipping_postal: string;
  shipping_country: string;
  success_url: string;
  cancel_url: string;
  simulate_payment?: boolean;
}

/* ----------------------------- CONFIG ----------------------------- */

const API_BASE =
  process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "http://localhost:8080";

const cookies = new Cookies();

function getAccessToken(): string | null {
  const token = cookies.get("access_token");
  if (typeof token !== "string") return null;
  const t = token.trim();
  return t || null;
}

function authHeaders(token?: string | null): Record<string, string> {
  const t = token ?? getAccessToken();
  return {
    "Content-Type": "application/json",
    ...(t ? { Authorization: `Bearer ${t}` } : {}),
  };
}

async function parseError(res: Response): Promise<string> {
  try {
    const data = await res.json();
    if (typeof data?.detail === "string") return data.detail;
    if (Array.isArray(data?.detail)) {
      return data.detail.map((d: any) => d.msg || JSON.stringify(d)).join("; ");
    }
    return data?.message || data?.error || JSON.stringify(data) || "Er ging iets mis";
  } catch {
    return "Er ging iets mis";
  }
}

function toWebSocketUrl(baseUrl: string, path: string): string {
  try {
    const url = new URL(baseUrl);
    url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
    const basePath = url.pathname.replace(/\/$/, "");
    const extraPath = path.startsWith("/") ? path : `/${path}`;
    url.pathname = `${basePath}${extraPath}`;
    return url.toString();
  } catch {
    const wsBase = baseUrl
      .replace(/^https:\/\//, "wss://")
      .replace(/^http:\/\//, "ws://")
      .replace(/\/$/, "");
    const extraPath = path.startsWith("/") ? path : `/${path}`;
    return `${wsBase}${extraPath}`;
  }
}

function normalizeLiveBid(raw: unknown): LiveBid | null {
  if (!raw || typeof raw !== "object") return null;
  const bid = raw as Partial<LiveBid> & Record<string, any>;
  const amount = Number(bid.amount);
  if (!Number.isFinite(amount)) return null;

  // Country can come flat (bid.country) or nested under bid.bidder.country
  const bidderObj = bid.bidder && typeof bid.bidder === "object" ? (bid.bidder as Record<string, any>) : undefined;
  const country =
    typeof bid.country === "string" || bid.country === null
      ? bid.country
      : typeof bidderObj?.country === "string" || bidderObj?.country === null
        ? bidderObj.country
        : undefined;

  const initials =
    typeof bid.initials === "string"
      ? bid.initials
      : typeof bidderObj?.initials === "string"
        ? bidderObj.initials
        : undefined;

  return {
    amount,
    bidder_id: String(bid.bidder_id ?? ""),
    initials,
    obfuscated_name: String(bid.obfuscated_name ?? "Anonymous"),
    timestamp: typeof bid.timestamp === "string" ? bid.timestamp : null,
    country,
  };
}

function normalizeAuctionStateMessage(raw: unknown): AuctionStateMessage | null {
  if (!raw || typeof raw !== "object") return null;
  const msg = raw as Partial<AuctionStateMessage>;
  if (msg.type !== "auction_state" && msg.type !== "new_bid") return null;

  const currentPrice = Number(msg.current_price);
  if (!Number.isFinite(currentPrice)) return null;

  const bids = Array.isArray(msg.bids)
    ? msg.bids.map((bid) => normalizeLiveBid(bid)).filter((bid): bid is LiveBid => bid !== null)
    : [];

  return {
    type: msg.type,
    current_price: currentPrice,
    end_time: typeof msg.end_time === "string" ? msg.end_time : null,
    status: typeof msg.status === "string" ? msg.status : "active",
    bids,
  };
}

function normalizeNewBidMessage(raw: unknown): NewBidMessage | null {
  if (!raw || typeof raw !== "object") return null;
  const msg = raw as Partial<NewBidMessage>;
  if (msg.type !== "new_bid") return null;

  const amount = Number(msg.amount);
  if (!Number.isFinite(amount)) return null;

  return {
    type: "new_bid",
    amount,
    bidder_id: typeof msg.bidder_id === "string" ? msg.bidder_id : undefined,
    timestamp: typeof msg.timestamp === "string" ? msg.timestamp : null,
    initials: typeof msg.initials === "string" ? msg.initials : undefined,
    obfuscated_name:
      typeof msg.obfuscated_name === "string" ? msg.obfuscated_name : undefined,
    country:
      typeof msg.country === "string" || msg.country === null
        ? msg.country
        : undefined,
    end_time: typeof msg.end_time === "string" ? msg.end_time : null,
  };
}

/* ----------------------------- REAL API LAYER ----------------------------- */

async function apiGetAuction(auctionId: string): Promise<Auction> {
  const res = await fetch(`${API_BASE}/auctions/${auctionId}`, {
    method: "GET",
    headers: authHeaders(),
    credentials: "include",
  });
  if (!res.ok) throw new Error(await parseError(res));
  return (await res.json()) as Auction;
}

async function apiGetAuctionByShirtId(shirtId: string): Promise<Auction> {
  const res = await fetch(`${API_BASE}/auctions/shirt/${shirtId}`, {
    method: "GET",
    headers: authHeaders(),
    credentials: "include",
  });
  if (!res.ok) throw new Error(await parseError(res));
  return (await res.json()) as Auction;
}

async function apiGetAuctionBids(auctionId: string): Promise<Bid[]> {
  const res = await fetch(`${API_BASE}/auctions/${auctionId}/bids`, {
    method: "GET",
    headers: authHeaders(),
    credentials: "include",
  });
  if (!res.ok) return []; // endpoint may not exist yet

  const data = await res.json();
  if (!Array.isArray(data)) return [];

  // Back-end can return bidder metadata either flat or nested under bidder.
  // Rows with non-finite amounts are filtered out rather than silently defaulted
  // to 0, which would mask invalid backend data and mislead the UI.
  return data.flatMap((raw): Bid[] => {
    const row = (raw ?? {}) as Record<string, any>;
    const amount = Number(row.amount);
    if (!Number.isFinite(amount)) return [];

    const bidderObj = row.bidder && typeof row.bidder === "object" ? row.bidder : undefined;
    const initials =
      typeof row.initials === "string"
        ? row.initials
        : typeof bidderObj?.initials === "string"
          ? bidderObj.initials
          : undefined;
    const bidderCountry =
      typeof row.country === "string" || row.country === null
        ? row.country
        : typeof bidderObj?.country === "string" || bidderObj?.country === null
          ? bidderObj.country
          : undefined;

    return [
      {
        id: String(row.id ?? ""),
        auction_id: typeof row.auction_id === "string" ? row.auction_id : auctionId,
        bidder_id: typeof row.bidder_id === "string" ? row.bidder_id : undefined,
        initials,
        country: bidderCountry,
        amount,
        created_at: typeof row.created_at === "string" ? row.created_at : "",
      },
    ];
  });
}

async function apiListAuctions(): Promise<Auction[]> {
  const res = await fetch(`${API_BASE}/auctions/`, {
    method: "GET",
    headers: authHeaders(),
    credentials: "include",
  });
  if (!res.ok) throw new Error(await parseError(res));
  return (await res.json()) as Auction[];
}

async function apiCreateAuction(payload: CreateAuctionPayload): Promise<Auction> {
  const res = await fetch(`${API_BASE}/auctions/`, {
    method: "POST",
    headers: authHeaders(),
    credentials: "include",
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return (await res.json()) as Auction;
}

async function apiPlaceBid(auctionId: string, amount: number): Promise<Bid> {
  console.log(`Placing bid of ${amount} on auction ${auctionId}`);
  console.log("Using endpoint:", `${API_BASE}/auctions/${auctionId}/bid`);
  const res = await fetch(`${API_BASE}/auctions/${auctionId}/bid`, {
    method: "POST",
    headers: authHeaders(),
    credentials: "include",
    body: JSON.stringify({ amount }),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return (await res.json()) as Bid;
}

async function apiEndAuction(auctionId: string): Promise<Auction> {
  const res = await fetch(`${API_BASE}/auctions/${auctionId}/end`, {
    method: "POST",
    headers: authHeaders(),
    credentials: "include",
  });
  if (!res.ok) throw new Error(await parseError(res));
  return (await res.json()) as Auction;
}

async function apiGetWonAuctions(): Promise<Auction[]> {
  const res = await fetch(`${API_BASE}/auctions/won`, {
    method: "GET",
    headers: authHeaders(),
    credentials: "include",
  });
  if (!res.ok) throw new Error(await parseError(res));
  return (await res.json()) as Auction[];
}

async function apiGetMyAuctionHistory(): Promise<MyAuctionHistory> {
  const res = await fetch(`${API_BASE}/auctions/my/history`, {
    method: "GET",
    headers: authHeaders(),
    credentials: "include",
  });
  if (!res.ok) throw new Error(await parseError(res));
  const data = await res.json();
  return {
    created: Array.isArray(data.created_auctions) ? data.created_auctions
           : Array.isArray(data.created)           ? data.created
           : [],
    active_bids: Array.isArray(data.active_bids) ? data.active_bids : [],
    won: Array.isArray(data.won_auctions) ? data.won_auctions
       : Array.isArray(data.won)          ? data.won
       : [],
  };
}


function apiGetLiveBids(auctionId: string, handlers: LiveBidsHandlers = {}): () => void {
  const SOCKET_URL = toWebSocketUrl(API_BASE, `/auctions/${auctionId}/live`);
  let lastState: AuctionLiveState | null = null;
  let destroyed = false;
  let retryDelay = 1000;
  let consecutiveImmediateFailures = 0;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let pollTimer: ReturnType<typeof setTimeout> | null = null;
  let socket: WebSocket;
  let pingInterval: ReturnType<typeof setInterval>;

  // Fallback: poll REST API for latest bids when WS keeps failing immediately
  function startPolling() {
    if (destroyed || pollTimer) return;
    console.log("[WS] Switching to REST polling fallback");
    const poll = () => {
      if (destroyed) return;
      apiGetAuctionBids(auctionId)
        .then((bids) => {
          if (bids.length > 0 && !destroyed) {
            const liveBids: LiveBid[] = bids.map((b: any) => ({
              amount: b.amount,
              bidder_id: b.bidder_id ?? "",
              initials: b.initials ?? b.bidder?.initials ?? undefined,
              obfuscated_name: "",
              timestamp: b.created_at ?? null,
              country: b.bidder?.country ?? b.country ?? undefined,
            }));
            handlers.onState?.({
              currentPrice: liveBids[0]?.amount ?? lastState?.currentPrice ?? 0,
              endTime: lastState?.endTime ?? null,
              status: lastState?.status ?? "active",
              bids: liveBids,
              highestBid: liveBids[0] ?? null,
            });
          }
        })
        .catch(() => {})
        .finally(() => {
          if (!destroyed) pollTimer = setTimeout(poll, 10000);
        });
    };
    pollTimer = setTimeout(poll, 5000);
  }

  const emitState = (state: AuctionLiveState) => {
    lastState = state;
    handlers.onState?.(state);
  };

  function connect() {
    if (destroyed) return;
    console.log(`Connecting to live auction feed at ${SOCKET_URL}`);
    socket = new WebSocket(SOCKET_URL);
    const connectTime = Date.now();

    socket.onmessage = (event) => {
      // Reset backoff on successful message
      retryDelay = 1000;
      consecutiveImmediateFailures = 0;

      let data: unknown;
      try {
        data = JSON.parse(event.data);
      } catch {
        return;
      }

      const stateMessage = normalizeAuctionStateMessage(data);
      if (stateMessage) {
        emitState({
          currentPrice: stateMessage.current_price,
          endTime: stateMessage.end_time,
          status: stateMessage.status,
          bids: stateMessage.bids,
          highestBid: stateMessage.bids[0] ?? null,
        });
        handlers.onMessage?.(stateMessage);
        return;
      }

      const newBidMessage = normalizeNewBidMessage(data);
      if (newBidMessage) {
        const bid: LiveBid = {
          amount: newBidMessage.amount,
          bidder_id: newBidMessage.bidder_id ?? "",
          initials: newBidMessage.initials,
          obfuscated_name: newBidMessage.obfuscated_name ?? "Anonymous",
          timestamp: newBidMessage.timestamp ?? null,
          country: newBidMessage.country ?? undefined,
        };

        const existingBids = lastState?.bids ?? [];
        const deduped = existingBids.filter(
          (existing) =>
            !(
              existing.amount === bid.amount &&
              existing.bidder_id === bid.bidder_id &&
              existing.timestamp === bid.timestamp
            ),
        );

        const bids = [bid, ...deduped];
        emitState({
          currentPrice: bid.amount,
          endTime: newBidMessage.end_time ?? lastState?.endTime ?? null,
          status: lastState?.status ?? "active",
          bids,
          highestBid: bids[0] ?? null,
        });
        handlers.onMessage?.(newBidMessage);
        return;
      }

      const msg = data as Record<string, unknown>;
      if (msg.type === "auction_extended" && typeof msg.new_end_time === "string") {
        emitState({
          currentPrice: lastState?.currentPrice ?? 0,
          endTime: msg.new_end_time,
          status: lastState?.status ?? "active",
          bids: lastState?.bids ?? [],
          highestBid: lastState?.highestBid ?? null,
        });
        handlers.onMessage?.(data as AuctionExtendedMessage);
        return;
      }

      handlers.onMessage?.(data as AuctionLiveMessage);
    };

    socket.onerror = (event) => {
      console.log(`[WS] Error`, event);
      handlers.onError?.(event);
    };

    socket.onopen = () => {
      console.log(`[WS] Connected to ${SOCKET_URL}`);
    };

    socket.onclose = (event) => {
      console.log(`[WS] Closed — code: ${event.code}, reason: "${event.reason}", wasClean: ${event.wasClean}`);
      handlers.onClose?.(event);

      if (destroyed) return;

      // If the auction is already ended/sold, stop reconnecting
      if (lastState?.status === "ended" || lastState?.status === "sold") {
        console.log("[WS] Auction ended — stopping reconnect");
        return;
      }

      // Detect "immediate disconnect" pattern: connected but closed within 2s without a message
      const aliveMs = Date.now() - connectTime;
      if (aliveMs < 2000) {
        consecutiveImmediateFailures++;
        console.log(`[WS] Immediate disconnect #${consecutiveImmediateFailures}`);
        // After 3 immediate failures, switch to polling (backend likely closed auction)
        if (consecutiveImmediateFailures >= 3) {
          console.log("[WS] Too many immediate failures — switching to polling");
          startPolling();
          return;
        }
      } else {
        consecutiveImmediateFailures = 0;
      }

      console.log(`[WS] Reconnecting in ${retryDelay}ms…`);
      retryTimer = setTimeout(() => {
        retryDelay = Math.min(retryDelay * 2, 30000);
        connect();
      }, retryDelay);
    };
  }

  connect();

  return () => {
    destroyed = true;
    clearInterval(pingInterval);
    if (retryTimer) clearTimeout(retryTimer);
    if (pollTimer) clearTimeout(pollTimer);
    if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
      socket.close();
    }
  };
}

async function apiCheckoutAuctionEscrow(payload: CheckoutAuctionEscrowPayload): Promise<unknown> {
  const res = await fetch(`${API_BASE}/orders/checkout-auction-escrow`, {
    method: "POST",
    headers: authHeaders(),
    credentials: "include",
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return await res.json();
}

/* ----------------------------- PUBLIC SERVICE ----------------------------- */

export const auctionService = {
  /** Lijst alle actieve auctions */
  async listAuctions(): Promise<Auction[]> {
    const auctions = await apiListAuctions();
    const now = new Date();
    return auctions.filter(
      (a) => a.status === "active" && new Date(a.end_time) > now,
    );
  },

  /** Haal een enkele auction op via ID */
  async getAuction(auctionId: string): Promise<Auction> {
    return apiGetAuction(auctionId);
  },

  /** Haal de actieve of meest recente auction op voor een specifiek shirt */
  async getAuctionByShirtId(shirtId: string): Promise<Auction> {
    return apiGetAuctionByShirtId(shirtId);
  },

  /** Haal bids op voor een auction */
  async getAuctionBids(auctionId: string): Promise<Bid[]> {
    return apiGetAuctionBids(auctionId);
  },

  /** Start een nieuwe auction voor een shirt */
  async createAuction(payload: CreateAuctionPayload): Promise<Auction> {
    return apiCreateAuction(payload);
  },

  /** Plaats een bod op een auction */
  async placeBid(auctionId: string, amount: number): Promise<Bid> {
    return apiPlaceBid(auctionId, amount);
  },

  /** Beëindig een auction vroegtijdig (seller only) */
  async endAuction(auctionId: string): Promise<Auction> {
    return apiEndAuction(auctionId);
  },

  /** Bekijk gewonnen auctions (buyer) */
  async getWonAuctions(): Promise<Auction[]> {
    return apiGetWonAuctions();
  },

  /** Haal de persoonlijke auction history op (aangemaakt, bids, gewonnen) */
  async getMyAuctionHistory(): Promise<MyAuctionHistory> {
    return apiGetMyAuctionHistory();
  },

  /** Betaal voor een gewonnen auction via escrow */
  async checkoutAuctionEscrow(payload: CheckoutAuctionEscrowPayload): Promise<unknown> {
    return apiCheckoutAuctionEscrow(payload);
  },

  getLiveBids(auctionId: string, handlers: LiveBidsHandlers = {}): () => void {
    return apiGetLiveBids(auctionId, handlers);
  },
};
